"use client";

import { HeaderSearchDropdownBody } from "@/components/layout/header-search-dropdown-body";
import { useSearch } from "@/components/layout/search-context";
import { useSearchListingContext } from "@/components/layout/search-listing-context";
import { Input } from "@/components/ui/input";
import { type AbandonReason, useSearchAnalytics } from "@/hooks/use-search-analytics";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { MAX_SEARCH_LENGTH, MIN_SEARCH_LENGTH } from "@/lib/constants/search";
import {
  HEADER_SEARCH_MIN_WIDTH_LG,
  HEADER_SEARCH_MIN_WIDTH_MD,
  HEADER_SEARCH_MIN_WIDTH_SM,
  headerSearchResultsCap,
} from "@/lib/header-search-layout";
import { inferPageOriginFromPathname } from "@/lib/header-search-page-origin";
import { parseSearchCatalogResponse } from "@/lib/search-catalog-response";
import { searchAndRankGames } from "@/lib/search-games-ordering";
import { createSearchSessionId } from "@/lib/search-session-id";
import type { GameWithRelations } from "@/types/game";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { usePathname } from "next/navigation";
import {
  type ElementRef,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

interface HeaderSearchProps {
  onActiveChange?: (isActive: boolean) => void;
}

function useSearchCatalogLoader(showDropdown: boolean) {
  const [catalog, setCatalog] = useState<GameWithRelations[] | null>(null);
  const [catalogLoadFailed, setCatalogLoadFailed] = useState(false);

  const loadCatalog = useCallback(async () => {
    if (catalog !== null || catalogLoadFailed) return;
    try {
      const res = await fetch("/api/games/search-catalog");
      if (!res.ok) throw new Error(String(res.status));
      const raw: unknown = await res.json();
      setCatalog(parseSearchCatalogResponse(raw));
    } catch {
      setCatalogLoadFailed(true);
      setCatalog([]);
    }
  }, [catalog, catalogLoadFailed]);

  useEffect(() => {
    if (showDropdown) {
      void loadCatalog();
    }
  }, [showDropdown, loadCatalog]);

  return { catalog, catalogLoadFailed };
}

export function HeaderSearch({ onActiveChange }: HeaderSearchProps) {
  const pathname = usePathname();
  const { pageOrigin: listingOrigin, priorityGameIds } = useSearchListingContext();
  const pageOrigin = listingOrigin ?? inferPageOriginFromPathname(pathname);
  const { search, setSearch } = useSearch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [searchSessionId, setSearchSessionId] = useState<string | null>(null);
  const [isComposing, setIsComposing] = useState(false);
  // Pixel offset for the scrim/results panel: the bottom edge of the sticky header.
  // Measured live so it accounts for the announcement bar (which sits above the
  // header and scrolls away) and the mobile auto-hide header.
  const [overlayTop, setOverlayTop] = useState(64);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<ElementRef<"section">>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isExpandedRef = useRef(false);
  const pendingFocusOnExpandRef = useRef(false);
  const searchRef = useRef("");

  const isActive = isExpanded;
  const isSearching = search.trim().length >= MIN_SEARCH_LENGTH;
  const showDropdown = isExpanded && isSearching;

  const { catalog, catalogLoadFailed } = useSearchCatalogLoader(showDropdown);

  const isMinWidthSm = useMediaQuery(HEADER_SEARCH_MIN_WIDTH_SM);
  const isMinWidthMd = useMediaQuery(HEADER_SEARCH_MIN_WIDTH_MD);
  const isMinWidthLg = useMediaQuery(HEADER_SEARCH_MIN_WIDTH_LG);

  const uncappedRankedGames = useMemo(() => {
    if (catalog === null || !isSearching) return [];
    return searchAndRankGames(catalog, search, priorityGameIds);
  }, [catalog, search, isSearching, priorityGameIds]);

  const rankedGames = useMemo(() => {
    const cap = headerSearchResultsCap(isMinWidthSm, isMinWidthMd, isMinWidthLg);
    return uncappedRankedGames.slice(0, cap);
  }, [uncappedRankedGames, isMinWidthSm, isMinWidthMd, isMinWidthLg]);

  const uncappedResultCount = uncappedRankedGames.length;
  const resultsReady = catalog !== null || catalogLoadFailed;

  const { trackResultClick, trackAbandon } = useSearchAnalytics({
    searchSessionId,
    rawQuery: search,
    page: pathname ?? "",
    pageOrigin,
    uncappedResultCount,
    isComposing,
    resultsReady,
    active: showDropdown,
  });

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Keep the scrim/results aligned to the live bottom of the header so the dim
  // overlay never covers the announcement bar or leaves a gap under the header.
  useEffect(() => {
    if (!showDropdown) return;
    const updateOverlayTop = () => {
      const header = document.getElementById("site-header");
      const bottom = header?.getBoundingClientRect().bottom ?? 64;
      setOverlayTop(Math.max(0, Math.round(bottom)));
    };
    updateOverlayTop();
    window.addEventListener("scroll", updateOverlayTop, { passive: true });
    window.addEventListener("resize", updateOverlayTop);
    return () => {
      window.removeEventListener("scroll", updateOverlayTop);
      window.removeEventListener("resize", updateOverlayTop);
    };
  }, [showDropdown]);

  const clearBlurTimeout = () => {
    if (!blurTimeoutRef.current) return;
    clearTimeout(blurTimeoutRef.current);
    blurTimeoutRef.current = null;
  };

  const clearExpandTimers = () => {
    pendingFocusOnExpandRef.current = false;
    clearBlurTimeout();
  };

  const beginSearchSession = () => {
    setSearchSessionId(createSearchSessionId());
  };

  const collapse = (abandonReason?: AbandonReason) => {
    if (abandonReason) {
      trackAbandon(abandonReason);
    }
    clearExpandTimers();
    isExpandedRef.current = false;
    setIsExpanded(false);
  };

  const dismissSearch = useCallback(() => {
    trackAbandon("escape");
    clearExpandTimers();
    setSearch("");
    isExpandedRef.current = false;
    setIsExpanded(false);
    inputRef.current?.blur();
  }, [setSearch, trackAbandon]);

  const handleResultSelect = useCallback(
    (game: GameWithRelations, position: number) => {
      trackResultClick(game, position);
      clearExpandTimers();
      isExpandedRef.current = false;
      setIsExpanded(false);
      inputRef.current?.blur();
    },
    [trackResultClick]
  );

  useEffect(() => {
    onActiveChange?.(isActive);
  }, [isActive, onActiveChange]);

  useLayoutEffect(() => {
    isExpandedRef.current = isExpanded;
    searchRef.current = search;
  }, [isExpanded, search]);

  useLayoutEffect(() => {
    if (!isExpanded || !pendingFocusOnExpandRef.current) return;
    pendingFocusOnExpandRef.current = false;

    const focusInput = () => {
      inputRef.current?.focus({ preventScroll: true });
    };

    focusInput();
    // Re-focus after paint so mobile header layout changes (logo hide) do not steal focus.
    const rafId = requestAnimationFrame(focusInput);
    return () => cancelAnimationFrame(rafId);
  }, [isExpanded]);

  useEffect(() => {
    const handleDocumentClickCapture = (event: MouseEvent) => {
      if (!isExpandedRef.current) return;

      const container = containerRef.current;
      const panel = panelRef.current;
      const target = event.target as Node | null;
      if (!target) return;
      if (container?.contains(target) || panel?.contains(target)) return;

      collapse("outside_click");
    };

    document.addEventListener("click", handleDocumentClickCapture, true);
    return () => {
      document.removeEventListener("click", handleDocumentClickCapture, true);
    };
  }, []);

  const handleIconClick = () => {
    beginSearchSession();
    pendingFocusOnExpandRef.current = true;
    isExpandedRef.current = true;
    setIsExpanded(true);
  };

  const handleSearchChange = (value: string) => {
    const validatedValue =
      value.length > MAX_SEARCH_LENGTH ? value.slice(0, MAX_SEARCH_LENGTH) : value;
    setSearch(validatedValue);
  };

  const handleFocus = () => {
    if (!isExpandedRef.current) {
      beginSearchSession();
    }
    isExpandedRef.current = true;
    setIsExpanded(true);
  };

  const handleBlur = () => {
    clearBlurTimeout();
    blurTimeoutRef.current = setTimeout(() => {
      if (!search.trim()) {
        collapse("blur_collapse");
      }
      blurTimeoutRef.current = null;
    }, 200);
  };

  useEffect(() => {
    if (!isExpanded && blurTimeoutRef.current) {
      clearBlurTimeout();
    }
  }, [isExpanded]);

  useEffect(() => {
    return () => {
      clearExpandTimers();
    };
  }, []);

  useEffect(() => {
    if (!showDropdown) return;
    const onDocumentKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      dismissSearch();
    };
    document.addEventListener("keydown", onDocumentKeyDown);
    return () => {
      document.removeEventListener("keydown", onDocumentKeyDown);
    };
  }, [showDropdown, dismissSearch]);

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      dismissSearch();
    }
  };

  const handleClear = () => {
    trackAbandon("cleared");
    setSearch("");
    beginSearchSession();
    inputRef.current?.focus();
  };

  const dropdownOverlay =
    isClient && showDropdown ? (
      <>
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: dimming scrim; Escape closes via document listener. */}
        <div
          data-testid="search-scrim"
          className="fixed inset-x-0 bottom-0 z-[45] bg-black/55"
          style={{ top: overlayTop }}
          role="presentation"
          aria-hidden
          onClick={() => {
            collapse("outside_click");
          }}
        />
        <div
          className="fixed inset-x-0 z-[60] flex justify-center px-3 pt-2 sm:px-4 sm:pt-3 pointer-events-none"
          style={{ top: overlayTop }}
        >
          <section
            ref={panelRef}
            id="header-search-results"
            aria-label="Search results"
            onMouseDown={(e) => e.preventDefault()}
            className="pointer-events-auto w-full max-w-[1024px] max-h-[min(65vh,28rem)] overflow-y-auto overscroll-contain rounded-xl border border-white/10 bg-ink-neutral shadow-2xl"
          >
            <HeaderSearchDropdownBody
              catalog={catalog}
              catalogLoadFailed={catalogLoadFailed}
              rankedGames={rankedGames}
              isSearching={isSearching}
              searchQuery={search.trim()}
              pageOrigin={pageOrigin}
              onResultSelect={handleResultSelect}
            />
          </section>
        </div>
      </>
    ) : null;

  return (
    <>
      <div
        ref={containerRef}
        className={`relative transition-all duration-300 ease-in-out ${isExpanded ? "w-full md:max-w-[280px]" : "ml-auto w-[40px] max-w-[40px] md:ml-auto"}`}
      >
        <button
          type="button"
          onClick={handleIconClick}
          className={`absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center text-white hover:text-gray-300 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 rounded-full z-10 transition-opacity duration-300 ${isExpanded ? "opacity-60 pointer-events-none" : "opacity-100 pointer-events-auto"}`}
          aria-label="Search games"
          aria-expanded={isExpanded}
          aria-controls="header-search-results"
        >
          <MagnifyingGlass weight="bold" size={18} />
        </button>

        <Input
          ref={inputRef}
          type="text"
          placeholder="Search games"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          autoComplete="off"
          aria-autocomplete="list"
          aria-controls={showDropdown ? "header-search-results" : undefined}
          className={`h-10 w-full rounded-full bg-white/10 text-white placeholder:text-white/60 border-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:[box-shadow:inset_0_0_0_2px_rgb(255_255_255_/_0.15)] pl-10 pr-10 text-base transition-opacity duration-200 ease-in-out ${
            isExpanded
              ? "opacity-100 visible pointer-events-auto"
              : "opacity-0 invisible pointer-events-none"
          }`}
          aria-label="Search games"
          aria-hidden={!isExpanded}
        />

        {search && isExpanded && (
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded-full p-1"
            aria-label="Clear search"
            onClick={handleClear}
          >
            <X weight="bold" size={16} />
          </button>
        )}
      </div>
      {dropdownOverlay ? createPortal(dropdownOverlay, document.body) : null}
    </>
  );
}
